import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  BASE_ELEMENT_DIRECTIVE_INPUTS,
  BaseElementDirective,
} from '../base-element-directive';
import { RadioLabelPosition, RadioOption } from './types';

@Component({
  selector: 'app-radio',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatRadioModule,
    MatTooltipModule,
    FormsModule,
    TranslocoPipe,
  ],
  hostDirectives: [
    {
      directive: BaseElementDirective,
      inputs: BASE_ELEMENT_DIRECTIVE_INPUTS,
      outputs: ['dataChange'],
    },
  ],
  templateUrl: './radio.component.html',
  styleUrl: './radio.component.scss',
})
export class RadioComponent<T = unknown> implements OnChanges {
  baseElement = inject(BaseElementDirective<unknown>);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Current selected value for the group. */
  @Input() value: T | null = null;
  /** Checked state for single radio usage (no options). */
  @Input() checked = false;
  /** Optional aria-label for single radio usage. */
  @Input() ariaLabel?: string;
  /** Optional name for the radio group */
  @Input() name?: string;
  /** Optional options for rendering a radio group inside this component. */
  @Input() options: RadioOption<T>[] = [];
  /** Position of the label relative to the radio circle: 'left' | 'right' | 'top'. Default is 'right'. */
  @Input() labelPosition: RadioLabelPosition = 'right';
  /** Allows clicking the selected option again to clear the group value. */
  @Input() allowDeselect = false;
  /** Emits when the radio selection changes. */
  @Output() valueChange = new EventEmitter<T | null>();
  /** Emits the native MatRadioChange event (single radio usage). */
  @Output() changed = new EventEmitter<MatRadioChange>();
  /** Emits when the single radio is clicked. */
  @Output() clicked = new EventEmitter<MouseEvent>();
  /** Optional CSS class(es) for the mat-radio-group when using options. */
  @Input() groupClass = '';

  @Input() disabledExternal: boolean | null | undefined = undefined;

  /** Maps our labelPosition to Material's 'before' | 'after' (top uses 'after' + wrapper CSS). */
  get materialLabelPosition(): 'before' | 'after' {
    return this.labelPosition === 'left' ? 'before' : 'after';
  }

  get isDisabled(): boolean {
    return this.baseElement.isDisabled;
  }

  get isDisabledByPermission(): boolean {
    return !this.baseElement.canUpdate() || false;
  }

  get resolvedOptions() {
    return this.options?.length
      ? this.options
      : [{ value: this.value, label: this.ariaLabel ?? '' }];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['options']) {
      this.cdr.markForCheck();
    }
  }

  onGroupChange(event: MatRadioChange): void {
    const newValue = event.value as T;
    this.valueChange.emit(newValue);
    this.baseElement.emitDataChange(newValue);
  }

  onOptionClick(event: MouseEvent, optionValue: T | null): void {
    if (!this.allowDeselect) {
      return;
    }

    const selectedValue = (this.baseElement.data ?? this.value) as T | null;
    if (selectedValue !== optionValue) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.valueChange.emit(null);
    this.baseElement.emitDataChange(null);
  }

  onSingleChange(event: MatRadioChange): void {
    this.changed.emit(event);
    this.valueChange.emit(event.value as T);
  }

  onSingleClick(event: MouseEvent): void {
    this.clicked.emit(event);
  }

  isOptionDisabled(option: RadioOption<T>): boolean {
    return (
      this.isDisabled ||
      this.isDisabledByPermission ||
      (option.disabled ?? false)
    );
  }
}
